import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import React, { useEffect, useState, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styles from './../../Dialog.module.sass'
import { useFetchList, useNoWaitFetchList } from 'store'
import { useDeepCompareEffect } from 'react-use'
import { zodResolver } from '@hookform/resolvers/zod'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { translateErrorMessage } from 'hooks/useSubmitTask'
import useSession from 'hooks/useSession'
import { useReload } from 'store'
import HFMedicalActsProtocol from 'components/HFInputs/HFMedicalActsProtocol'
import {
	Appointment,
	MedicalActProtocolRule,
	Options,
	Requisition,
} from 'models'
import { EditMedicalActProtocolSchema } from './schemas/EditMedicalActProtocolSchema'
import protocolMedicalActs from './ProtocolMedicalActs.json'
import { v4 as uuidv4 } from 'uuid'

const EditMedicalActProtocolDialog = (props) => {
	const { data, dismiss, resolve } = props
	const { processId, accidentType, postalCode } = data
	const { i18n, pushNotification } = useGlobal()
	const { logout } = useSession()
	const reload = useReload()
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)
	const [suggestionsLog, setSuggestionsLog] = useState({})
	const suggestionsLogRef = useRef({})
	const validateRef = useRef({})
	const [showWarning, setShowWarning] = useState(false)
	const [blockCount, setBlockCount] = useState(0)

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(EditMedicalActProtocolSchema),
		defaultValues: {
			medicalActs: [
				{
					uuid: uuidv4(),
					quantity: 1,
					medicalActId: '',
					serviceProviderId: '',
					sent: false,
				},
			],
		},
	})

	const availableProtocolMedicalActs = new Set(protocolMedicalActs)

	const { handleSubmit, formState, reset } = methods

	const providerEntitiesUri = '/metadata/provider_entities'
	const medicalActsUri = '/metadata/medical_acts'
	const medicalActProcessProtocolsUri = `/rules/clinical_process/${processId}?ruleType=AutomationRule&taskType=MedicalActsClinicalApproval`
	const processTasksUri = `/clinical_processes/${processId}/tasks`
	const requisitionsUri = `/clinical_processes/${processId}/requisitions`
	const processContentUri = `clinical_processes/${data?.processId}`
	const { contents } = useNoWaitFetchList(
		[`clinical_processes/${processId}/appointments`],
		[Appointment.fromJSON]
	)

	const {
		contents: [
			providerEntities,
			medicalActs,
			requisitions,
			medicalActProcessProtocols,
			processContent,
		],
	} = useFetchList(
		[
			providerEntitiesUri,
			medicalActsUri,
			requisitionsUri,
			medicalActProcessProtocolsUri,
			processContentUri,
		],
		[
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Requisition.fromJSON,
		]
	)

	const providerEntitiesOptions = [
		{ value: '', text: i18n`None` },
		...(providerEntities || []),
	]

	const availableProtocolMedicalActsOptions = medicalActs?.filter(
		({ text: ma }) => availableProtocolMedicalActs.has(ma.split(' ')?.[0])
	)

	const onSubmit = async (formData) => {
		if (submitting) return

		const hasBlocked = Object.values(validateRef.current).some(Boolean)
		if (hasBlocked) {
			setShowWarning(true)
			return
		}

		const { medicalActs } = formData
		const endpoint = `${API_URL}/api/rules/clinical_process/${processId}`

		const filteredMedicalActs = medicalActs?.filter(
			({ medicalActId }) => medicalActId !== ''
		)

		const medicalActsRequestBody =
			MedicalActProtocolRule.constructRequestBody(
				filteredMedicalActs,
				`Automação protocolo para processo: ${processId}`
			)

		setError(null)
		setSubmitting('submitting')

		try {
			await fetch(
				endpoint,
				fetchOptions({ method: 'PUT', body: medicalActsRequestBody })
			).then((r) => parseJSONContent(r))

			const logEntries = filteredMedicalActs
				.map((act) => {
					const suggestion =
						suggestionsLogRef.current[String(act.medicalActId)]
					if (!suggestion?.response?.providerReferrals?.length)
						return null
					return {
						...suggestion,
						providerId: act.serviceProviderId || null,
					}
				})
				.filter(Boolean)

			for (const act of logEntries) {
				try {
					const logRequestBodyWithoutRequest = {
						processId: processId,
						requestingProviderId: null,
						requisitionId: null,
						codes: String(act.actId),
						postalCode: postalCode,
						scheduleDate: null,
						appointmentDate: null,
						providers:
							act.response?.providerReferrals?.map((p) => ({
								providerId: p.providerId,
								profitProvider: Math.round(p.profit || 0),
								distance: p.distance || 0,
								speed: p.averageTime || 0,
							})) || [],
						chosenProvider: act.providerId || null,
						username:
							JSON.parse(localStorage.getItem('user'))
								?.username ?? null,
						justification:
							act.userJustification ||
							act.response?.description ||
							'Aprovação manual',
						userJustification: act.userJustification ?? null,
						userJustificationId: act.userJustificationId ?? null,
						suggestType: act.suggestType ?? 1,
					}

					const logRequestBody = {
						...logRequestBodyWithoutRequest,
						request: JSON.stringify(logRequestBodyWithoutRequest),
					}

					await fetch(`${API_URL}/api/suggest/logs`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify(logRequestBody),
					})
				} catch (error) {
					console.error(
						`Erro ao guardar log para ${act.actId}:`,
						error
					)
				}
			}

			const newActs = filteredMedicalActs
				.map((act) => {
					const suggestion = suggestionsLog[String(act.medicalActId)]
					if (
						!suggestion?.uuid ||
						!suggestion?.actId ||
						suggestion?.sent === true
					)
						return null
					return {
						...suggestion,
						providerId: act.serviceProviderId || null,
					}
				})
				.filter(Boolean)

			if (!newActs.length) {
				pushNotification(
					undefined,
					'info',
					i18n`No new medical acts to send`
				)
			} else {
				for (const act of newActs) {
					try {
						const logRequestBody = {
							processId: processId,
							requestingProviderId: null,
							requisitionId: null,
							codes: Array.isArray(act.medicalActCode)
								? act.medicalActCode.join(',')
								: String(act.medicalActCode ?? act.actId),
							postalCode: postalCode,
							scheduleDate: null,
							appointmentDate: null,
							providers:
								act.response?.providerReferrals?.map((p) => ({
									providerId: p.providerId,
									profitProvider: Math.round(p.profit || 0),
									distance: p.distance || 0,
									speed: p.averageTime || 0,
								})) || [],
							chosenProvider: act.providerId ?? null,
							username:
								JSON.parse(localStorage.getItem('user'))
									?.username ?? null,
							justification:
								act.userJustification ||
								act.response?.description ||
								'Protocolo automático',
							userJustification: act.userJustification ?? null,
							userJustificationId:
								act.userJustificationId ?? null,
							request: 'Protocol automation',
							suggestType: act.suggestType ?? 1,
						}

						const resp = await fetch(
							`${API_URL}/api/suggest/logs`,
							{
								method: 'POST',
								headers: { 'Content-Type': 'application/json' },
								body: JSON.stringify(logRequestBody),
							}
						)

						if (resp.ok) {
							const allActs =
								methods.getValues('medicalActs') || []
							const idx = allActs.findIndex(
								(f) => String(f.uuid) === String(act.uuid)
							)
							if (idx >= 0) {
								methods.setValue(
									`medicalActs.${idx}.sent`,
									true,
									{
										shouldDirty: true,
									}
								)
							}
						}
					} catch (error) {
						console.error(
							`Erro ao gravar log para novo ato ${act.actId}:`,
							error
						)
					}
				}
			}

			resolve && resolve()
			reload([medicalActProcessProtocolsUri, processTasksUri])
			pushNotification(
				undefined,
				'success',
				i18n`Process updated successfully!`
			)
		} catch (e) {
			console.error('Erro ao guardar:', e)
			if (e.status === '401' || e.status === 401) logout()
			const errorMessage = translateErrorMessage(e, i18n)
			setSubmitting(false)
			setError([errorMessage])
		}
	}

	useDeepCompareEffect(() => {
		if (medicalActProcessProtocols?.length) {
			const medicalActsRuleValuesObj =
				MedicalActProtocolRule.retrieveRuleValues(
					medicalActProcessProtocols
				)

			const withSent = medicalActsRuleValuesObj.map((m) => ({
				...m,
				sent: true,
			}))

			reset({
				medicalActs: withSent,
			})
		}
	}, [medicalActProcessProtocols || [], reset])

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState.errors, i18n])

	const injuredBirthday = processContent?.injured?.birthday
	const injuredType = processContent?.info?.injuryType?.id
	const bodyPart = processContent?.info?.bodyPart?.id

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			<FormProvider {...methods}>
				<form
					id={'form_process_protocol_edit'}
					onSubmit={handleSubmit(onSubmit)}
					className={`${styles['protocols-form'] || ''}`}
					disabled={submitting}
				>
					<fieldset>
						<HFMedicalActsProtocol
							data={{
								insuranceId:
									processContent?.insurancePolicy
										?.insuranceCompany?.id,
								providerEntities: providerEntitiesOptions,
								medicalActs:
									availableProtocolMedicalActsOptions,
								requisitions,
								processId,
								accidentType: accidentType,
								processPostalCode: postalCode,
								onUpdateFormData: () => {},
							}}
							name={'medicalActs'}
							onSuggestionsChange={(log) => {
								suggestionsLogRef.current = log
							}}
							injuredBirthday={injuredBirthday}
							injuredType={injuredType}
							bodyPart={bodyPart}
							blockCountDispatcher={(delta) =>
								setBlockCount((c) => c + delta)
							}
							validateRef={validateRef}
							showWarning={showWarning}
							onJustificationChanged={(
								actId,
								justification,
								justificationId
							) => {
								setShowWarning(false)
								if (!actId) return
								const key = String(actId)
								if (justification === null) {
									if (suggestionsLogRef.current[key]) {
										suggestionsLogRef.current[
											key
										].userJustification = null
										suggestionsLogRef.current[
											key
										].userJustificationId = null
									}
								} else {
									if (!suggestionsLogRef.current[key]) {
										suggestionsLogRef.current[key] = {}
									}
									suggestionsLogRef.current[
										key
									].userJustification = justification
									suggestionsLogRef.current[
										key
									].userJustificationId = justificationId
								}
							}}
						/>
					</fieldset>
				</form>
			</FormProvider>
			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton
					form={`form_process_protocol_edit`}
					disabled={submitting}
					primary
				>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Save`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default EditMedicalActProtocolDialog
